/**
 * Session Engine v3 — step navigator.
 *
 * Prerequisites = The Frame; primary goal = Main Event.
 * When the goal is S2, Frame also includes an Items (pool) facet so items can
 * be declared before S3/S4 without offering Go live.
 */

import { Check, Lock } from 'lucide-react';
import {
  canRunRanking,
  isStepContentSatisfied,
  isStepMandatory,
  isStepRunComplete,
  stepModeAvailability,
} from '../engineLogic';
import {
  buildStepRailEntries,
  sameNavTarget,
  type StepNavTarget,
} from '../stepFacets';
import { useSessionEngineStore } from '../store';
import { STEP_META, STEP_ORDER, type StepId } from '../types';

interface Props {
  active: StepNavTarget;
  onSelect: (target: StepNavTarget) => void;
}

export function StepRail({ active, onSelect }: Props) {
  const event = useSessionEngineStore((s) => s.event);
  if (!event) return null;

  const rankingLocked = !canRunRanking(event);
  // Say which thing is missing, not "complete prerequisites".
  const rankingLockReason =
    stepModeAvailability(event, 'S5', event.steps.S5.mode).reason ??
    'Add items in S2 before running the ranking.';
  const goalStep = event.primaryGoalStep;
  const requiredSteps = STEP_ORDER.filter((s) => event.steps[s].required);
  const { frame, mainEvent } = buildStepRailEntries(requiredSteps, goalStep);

  return (
    <nav className="space-y-1">
      {frame.length > 0 && (
        <p className="px-2 pb-1 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
          The Frame
        </p>
      )}
      {frame.map((target) => (
        <StepButton
          key={`${target.step}-${target.facet}`}
          target={target}
          active={active}
          onSelect={onSelect}
          locked={false}
        />
      ))}

      {mainEvent && (
        <>
          <p className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
            Main Event
          </p>
          <StepButton
            target={mainEvent}
            active={active}
            onSelect={onSelect}
            locked={mainEvent.step === 'S5' && rankingLocked}
            lockReason={rankingLockReason}
          />
        </>
      )}

      {!mainEvent &&
        requiredSteps.map((step) => {
          const isMainEvent = step === 'S5';
          return (
            <div key={step}>
              {isMainEvent && (
                <p className="px-2 pb-1 pt-3 text-[10px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
                  Main Event
                </p>
              )}
              <StepButton
                target={{ step, facet: isMainEvent ? 'goal' : 'frame' }}
                active={active}
                onSelect={onSelect}
                locked={isMainEvent && rankingLocked}
                lockReason={rankingLockReason}
              />
            </div>
          );
        })}
    </nav>
  );
}

function StepButton({
  target,
  active,
  onSelect,
  locked,
  lockReason,
}: {
  target: StepNavTarget;
  active: StepNavTarget;
  onSelect: (t: StepNavTarget) => void;
  locked: boolean;
  /** Plain-words explanation shown when the entry is locked. */
  lockReason?: string;
}) {
  const event = useSessionEngineStore((s) => s.event)!;
  const isGoalFacet = target.facet === 'goal';
  const isActive = sameNavTarget(active, target);

  const contentOk = isStepContentSatisfied(event, target.step);
  const runOk = isStepRunComplete(event, target.step);
  const satisfied = isGoalFacet
    ? event.steps[target.step].mode === 'collaborative'
      ? runOk
      : contentOk
    : contentOk;

  const isInherited = satisfied && !isGoalFacet && !isActive && target.step !== 'S2';
  const isFramePool = target.facet === 'frame' && target.step === 'S2' && event.primaryGoalStep === 'S2';

  const subtitle = isFramePool
    ? contentOk
      ? 'Item pool · ready'
      : 'Item pool · declare items'
    : isInherited
      ? 'Inherited from track'
      : isGoalFacet
        ? event.steps[target.step].mode === 'collaborative'
          ? 'Collaborative sitting'
          : 'Declared'
        : event.steps[target.step].mode === 'collaborative'
          ? 'Collaborative'
          : 'Declared';

  return (
    <button
      type="button"
      onClick={() => !locked && onSelect(target)}
      disabled={locked}
      title={locked ? lockReason ?? 'Complete prerequisites before running this step' : undefined}
      className={[
        'flex w-full items-center gap-3  px-3 py-2 text-left transition',
        locked ? 'cursor-not-allowed opacity-40' : 'cursor-pointer',
        isActive
          ? isGoalFacet
            ? 'bg-granite-gray-700 text-white ring-1 ring-electric-mustard-500/70'
            : 'bg-slate-blue-600 text-white'
          : isInherited
            ? 'text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]'
            : 'text-[var(--text-sec)] dark:text-[var(--text-sec)] hover:bg-[var(--hover-tint)] dark:hover:bg-[var(--hover-tint)]',
      ].join(' ')}
    >
      <span
        className={[
          'flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold',
          satisfied
            ? isActive
              ? isGoalFacet
                ? 'bg-electric-mustard-500/30 text-electric-mustard-300'
                : 'bg-[var(--bg-raised)]/20 text-white'
              : 'bg-alpine-green-100 text-alpine-green-700 dark:bg-alpine-green-900/40 dark:text-alpine-green-400'
            : isActive
              ? isGoalFacet
                ? 'bg-electric-mustard-500/20 text-electric-mustard-300'
                : 'bg-[var(--bg-raised)]/20 text-white'
              : 'bg-[var(--hover-tint)] text-[var(--text-ter)] dark:bg-[var(--surface-raised)] dark:text-[var(--text-ter)]',
        ].join(' ')}
      >
        {satisfied ? <Check size={13} /> : target.step.replace('S', '')}
      </span>
      <span className="flex-1">
        <span className="block text-sm font-medium">
          {isFramePool ? 'Items (pool)' : STEP_META[target.step].label}
        </span>
        <span
          className={[
            'block text-[11px]',
            isActive ? 'text-white/80' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]',
          ].join(' ')}
        >
          {subtitle}
          {!isStepMandatory(event, target.step) && !isFramePool ? ' · optional' : ''}
        </span>
      </span>
      {locked && <Lock size={13} className={isActive ? 'text-white/80' : 'text-[var(--text-ter)] dark:text-[var(--text-ter)]'} />}
      {!locked && !isActive && satisfied && (
        <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-ter)] hover:text-[var(--text-sec)] dark:hover:text-[var(--text-sec)]">
          edit
        </span>
      )}
    </button>
  );
}

/** @deprecated kept for typed imports that only need StepId */
export type { StepId };
